import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/landing.css";
import {
  FlaskConical,
  Zap,
  Shield,
  Users,
  BarChart3,
  Code,
  ArrowRight,
  Play,
  Star,
  Github,
  Twitter,
  Linkedin,
  CheckCircle2,
  Sparkles,
  TrendingUp,
  Clock,
  Layers,
  GitBranch,
  Cloud,
  Database,
  FileText,
  Workflow,
  Bug,
  Target,
  Rocket,
  Building2,
  Briefcase,
} from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [counters, setCounters] = useState({
    users: 0,
    testCases: 0,
    projects: 0,
    testsRun: 0,
  });
  const [isVisible, setIsVisible] = useState(false);

  // Force light theme on this page
  useEffect(() => {
    // Store original theme state
    const hadDarkClass = document.documentElement.classList.contains("dark");
    const savedTheme = localStorage.getItem("theme") || "LIGHT";

    // Force light theme
    document.documentElement.classList.remove("dark");

    // Restore original theme on unmount
    return () => {
      // Check if we should restore dark theme based on saved preference
      if (
        savedTheme === "DARK" ||
        (savedTheme === "SYSTEM" &&
          window.matchMedia("(prefers-color-scheme: dark)").matches)
      ) {
        document.documentElement.classList.add("dark");
      } else if (hadDarkClass && savedTheme !== "LIGHT") {
        // If it was dark before and not explicitly LIGHT, restore it
        document.documentElement.classList.add("dark");
      }
    };
  }, []);

  // Animated counter effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
            animateCounters();
          }
        });
      },
      { threshold: 0.3 },
    );

    const statsElement = document.querySelector(".stats-sidebar");
    if (statsElement) {
      observer.observe(statsElement);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  const animateCounters = () => {
    const targets = {
      users: 12500,
      testCases: 85000,
      projects: 3200,
      testsRun: 2500000,
    };
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    Object.keys(targets).forEach((key) => {
      let current = 0;
      const target = targets[key];
      const increment = target / steps;

      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          current = target;
          clearInterval(timer);
        }
        setCounters((prev) => ({ ...prev, [key]: Math.floor(current) }));
      }, stepDuration);
    });
  };

  const handleSignIn = () => {
    navigate("/login");
  };

  const handleGetStarted = () => {
    navigate("/register");
  };

  const features = [
    {
      icon: <FlaskConical className="w-8 h-8" />,
      title: "Test Case Management",
      description:
        "Organize, manage, and execute test cases with ease. Create comprehensive test suites and track execution results in real-time.",
      color: "from-purple-500 to-pink-500",
      gradient: "bg-gradient-to-br from-purple-50 to-pink-50",
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Zero-Code UI Automation",
      description:
        "Build powerful UI automation tests without writing a single line of code. Our visual test builder makes automation accessible to everyone.",
      color: "from-yellow-500 to-orange-500",
      gradient: "bg-gradient-to-br from-yellow-50 to-orange-50",
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Advanced Analytics",
      description:
        "Get detailed insights into your testing metrics. Track test coverage, pass rates, and identify bottlenecks in your testing process.",
      color: "from-blue-500 to-cyan-500",
      gradient: "bg-gradient-to-br from-blue-50 to-cyan-50",
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Team Collaboration",
      description:
        "Enable seamless collaboration between testers, developers, and stakeholders with role-based access and real-time updates.",
      color: "from-green-500 to-emerald-500",
      gradient: "bg-gradient-to-br from-green-50 to-emerald-50",
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Quality Assurance",
      description:
        "Ensure software quality with comprehensive testing workflows, defect tracking, and quality gates integrated into your pipeline.",
      color: "from-indigo-500 to-purple-500",
      gradient: "bg-gradient-to-br from-indigo-50 to-purple-50",
    },
    {
      icon: <Code className="w-8 h-8" />,
      title: "API Testing",
      description:
        "Test your APIs with our built-in API testing tools. Validate responses, test authentication, and ensure your services are reliable.",
      color: "from-red-500 to-rose-500",
      gradient: "bg-gradient-to-br from-red-50 to-rose-50",
    },
  ];

  const benefits = [
    { icon: <Clock className="w-5 h-5" />, text: "Save 40% testing time" },
    { icon: <TrendingUp className="w-5 h-5" />, text: "3x faster releases" },
    { icon: <CheckCircle2 className="w-5 h-5" />, text: "99.9% accuracy" },
    { icon: <Layers className="w-5 h-5" />, text: "Unlimited test cases" },
  ];

  const stats = [
    {
      value: counters.users,
      suffix: "+",
      label: "Active Users",
      icon: <Users className="w-6 h-6" />,
    },
    {
      value: counters.testCases,
      suffix: "+",
      label: "Test Cases Created",
      icon: <FileText className="w-6 h-6" />,
    },
    {
      value: counters.projects,
      suffix: "+",
      label: "Active Projects",
      icon: <Briefcase className="w-6 h-6" />,
    },
    {
      value: counters.testsRun,
      suffix: "+",
      label: "Tests Run Executed",
      icon: <Rocket className="w-6 h-6" />,
    },
  ];

  const additionalFeatures = [
    {
      icon: <GitBranch className="w-8 h-8" />,
      title: "CI/CD Integration",
      description:
        "Seamlessly integrate with Jenkins, GitLab CI, GitHub Actions, and more. Automate your testing pipeline.",
      color: "from-blue-500 to-indigo-500",
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: "Data-Driven Testing",
      description:
        "Create data-driven test cases with CSV, Excel, or database connections. Test multiple scenarios efficiently.",
      color: "from-teal-500 to-cyan-500",
    },
    {
      icon: <Workflow className="w-8 h-8" />,
      title: "Test Orchestration",
      description:
        "Design complex test workflows with conditional logic, parallel execution, and smart scheduling.",
      color: "from-violet-500 to-purple-500",
    },
    {
      icon: <Bug className="w-8 h-8" />,
      title: "Defect Management",
      description:
        "Track bugs from discovery to resolution. Link defects to test cases and maintain full traceability.",
      color: "from-red-500 to-pink-500",
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Requirements Traceability",
      description:
        "Map test cases to requirements and user stories. Ensure complete coverage and compliance.",
      color: "from-orange-500 to-amber-500",
    },
    {
      icon: <Cloud className="w-8 h-8" />,
      title: "Cloud & On-Premise",
      description:
        "Deploy on our cloud or your infrastructure. Full control with enterprise-grade security and compliance.",
      color: "from-sky-500 to-blue-500",
    },
  ];

  const allFeatures = [...features, ...additionalFeatures];

  const useCases = [
    {
      title: "Agile Development Teams",
      description:
        "Accelerate your sprint cycles with integrated test management",
      icon: <Rocket className="w-6 h-6" />,
      metrics: "50% faster sprints",
    },
    {
      title: "Enterprise QA",
      description: "Scale testing across multiple teams and projects",
      icon: <Building2 className="w-6 h-6" />,
      metrics: "10,000+ users",
    },
    {
      title: "DevOps Integration",
      description: "Automate testing in your CI/CD pipeline",
      icon: <Workflow className="w-6 h-6" />,
      metrics: "99.9% uptime",
    },
    {
      title: "Compliance Testing",
      description: "Meet regulatory requirements with audit trails",
      icon: <Shield className="w-6 h-6" />,
      metrics: "SOC 2 compliant",
    },
  ];

  const integrations = [
    { name: "Jira", icon: "🔷" },
    { name: "GitHub", icon: "🐙" },
    { name: "GitLab", icon: "🦊" },
    { name: "Jenkins", icon: "🤖" },
    { name: "Slack", icon: "💬" },
    { name: "Azure DevOps", icon: "☁️" },
    { name: "Confluence", icon: "📝" },
    { name: "Trello", icon: "📋" },
    { name: "Selenium", icon: "🌐" },
    { name: "Postman", icon: "📮" },
    { name: "Docker", icon: "🐳" },
    { name: "Kubernetes", icon: "⚓" },
  ];

  const pricingPlans = [
    {
      name: "Starter",
      price: "$29",
      period: "per month",
      description: "Perfect for small teams getting started",
      features: [
        "Up to 5 team members",
        "1,000 test cases",
        "Basic reporting",
        "Email support",
        "API access",
      ],
      popular: false,
    },
    {
      name: "Professional",
      price: "$99",
      period: "per month",
      description: "For growing teams and projects",
      features: [
        "Up to 25 team members",
        "Unlimited test cases",
        "Advanced analytics",
        "Priority support",
        "CI/CD integration",
        "Custom workflows",
      ],
      popular: true,
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For large organizations",
      features: [
        "Unlimited team members",
        "Unlimited everything",
        "Dedicated support",
        "On-premise deployment",
        "Custom integrations",
        "SLA guarantee",
      ],
      popular: false,
    },
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "QA Lead at TechCorp",
      content:
        "TestNimbus has revolutionized our testing process. The zero-code automation feature saved us months of development time.",
      avatar: "SJ",
      rating: 5,
      company: "TechCorp",
    },
    {
      name: "Mike Chen",
      role: "Software Engineer at StartupXYZ",
      content:
        "The best test management platform I've used. Intuitive interface and powerful features that actually improve our workflow.",
      avatar: "MC",
      rating: 5,
      company: "StartupXYZ",
    },
    {
      name: "Emily Rodriguez",
      role: "Testing Manager at Enterprise Inc",
      content:
        "Finally, a platform that scales with our needs. The analytics and reporting features are game-changers for our team.",
      avatar: "ER",
      rating: 5,
      company: "Enterprise Inc",
    },
    {
      name: "David Kim",
      role: "DevOps Lead at CloudScale",
      content:
        "The CI/CD integration is seamless. We've reduced our deployment time by 60% and increased confidence in releases.",
      avatar: "DK",
      rating: 5,
      company: "CloudScale",
    },
    {
      name: "Lisa Wang",
      role: "QA Director at FinTech Solutions",
      content:
        "Compliance and audit features are outstanding. We passed our SOC 2 audit with flying colors thanks to TestNimbus.",
      avatar: "LW",
      rating: 5,
      company: "FinTech Solutions",
    },
    {
      name: "James Miller",
      role: "CTO at InnovateLab",
      content:
        "ROI was immediate. We cut testing costs by 45% while improving coverage. Best investment we made this year.",
      avatar: "JM",
      rating: 5,
      company: "InnovateLab",
    },
  ];

  return (
    <div className="landing-page">
      {/* Animated Background Elements */}
      <div className="hero-background">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
      </div>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">
            <Sparkles className="w-4 h-4" />
            <span>Trusted by 10,000+ QA Teams Worldwide</span>
          </div>
          <h1 className="hero-title">
            Test Management Made
            <span className="gradient-text"> Simple</span>
          </h1>
          <p className="hero-description">
            The all-in-one platform for test case management, zero-code
            automation, and quality assurance. Streamline your testing workflow
            and deliver better software faster.
          </p>

          {/* Benefits List */}
          <div className="hero-benefits">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-item">
                {benefit.icon}
                <span>{benefit.text}</span>
              </div>
            ))}
          </div>

          <div className="hero-actions">
            <button
              onClick={handleGetStarted}
              className="btn-primary btn-large btn-glow"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
            <button className="btn-secondary btn-large btn-video">
              <Play className="w-5 h-5 mr-2" />
              Watch Demo
            </button>
          </div>
        </div>
      </section>

      {/* Main Content Section with Stats Sidebar and Features */}
      <section className="main-content-section">
        <div className="container main-content-wrapper">
          {/* Statistics Sidebar */}
          <aside className="stats-sidebar">
            {stats.map((stat, index) => (
              <div key={index} className="stat">
                <div className="stat-icon">{stat.icon}</div>
                <div className="stat-value">
                  {stat.value.toLocaleString()}
                  {stat.suffix}
                </div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
            <div className="stat stat-highlight">
              <div className="stat-value">99.9%</div>
              <div className="stat-label">Uptime SLA</div>
            </div>
          </aside>

          {/* Features Section */}
          <div className="features-main">
            <div className="section-header">
              <h2 className="section-title">Powerful Features</h2>
              <p className="section-description">
                Everything you need to manage your testing process from start to
                finish
              </p>
            </div>

            {/* Feature Tabs */}
            <div className="feature-tabs">
              <button
                className={`tab-button ${activeTab === "all" ? "active" : ""}`}
                onClick={() => setActiveTab("all")}
              >
                All Features
              </button>
              <button
                className={`tab-button ${activeTab === "core" ? "active" : ""}`}
                onClick={() => setActiveTab("core")}
              >
                Core Features
              </button>
              <button
                className={`tab-button ${activeTab === "advanced" ? "active" : ""}`}
                onClick={() => setActiveTab("advanced")}
              >
                Advanced
              </button>
            </div>

            <div className="features-grid">
              {(activeTab === "all"
                ? allFeatures
                : activeTab === "core"
                  ? features
                  : additionalFeatures
              ).map((feature, index) => (
                <div
                  key={index}
                  className="feature-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div
                    className={`feature-icon bg-gradient-to-br ${feature.color}`}
                  >
                    {feature.icon}
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                  <div className="feature-link">
                    Learn more <ArrowRight className="w-4 h-4 inline ml-1" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="use-cases-section">
        <div className="container">
          <h2 className="section-title">Built for Every Team</h2>
          <p className="section-description">
            TestNimbus adapts to your workflow, whether you're a startup or
            enterprise
          </p>
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card">
                <div className="use-case-icon">{useCase.icon}</div>
                <h3 className="use-case-title">{useCase.title}</h3>
                <p className="use-case-description">{useCase.description}</p>
                <div className="use-case-metric">{useCase.metrics}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="integrations-section">
        <div className="container">
          <h2 className="section-title">Integrations That Work</h2>
          <p className="section-description">
            Connect TestNimbus with your favorite tools and services
          </p>
          <div className="integrations-grid">
            {integrations.map((integration, index) => (
              <div key={index} className="integration-card">
                <div className="integration-icon">{integration.icon}</div>
                <div className="integration-name">{integration.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials">
        <div className="container">
          <div className="testimonials-header">
            <div>
              <h2 className="section-title">What Our Users Say</h2>
              <div className="testimonials-stats">
                <div className="testimonial-stat">
                  <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                  <span>4.9/5</span>
                </div>
                <div className="testimonial-stat">
                  <span>Based on 1,247 reviews</span>
                </div>
              </div>
            </div>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 fill-yellow-400 text-yellow-400"
                    />
                  ))}
                </div>
                <p className="testimonial-content">"{testimonial.content}"</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">{testimonial.avatar}</div>
                  <div>
                    <div className="testimonial-name">{testimonial.name}</div>
                    <div className="testimonial-role">{testimonial.role}</div>
                    <div className="testimonial-company">
                      {testimonial.company}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <div className="container">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-description">
            Choose the plan that fits your team. All plans include a 14-day free
            trial.
          </p>
          <div className="pricing-grid">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`pricing-card ${plan.popular ? "popular" : ""}`}
              >
                {plan.popular && (
                  <div className="popular-badge">Most Popular</div>
                )}
                <div className="pricing-header">
                  <h3 className="pricing-name">{plan.name}</h3>
                  <div className="pricing-price">
                    <span className="price-amount">{plan.price}</span>
                    {plan.period && (
                      <span className="price-period">/{plan.period}</span>
                    )}
                  </div>
                  <p className="pricing-description">{plan.description}</p>
                </div>
                <ul className="pricing-features">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="pricing-feature">
                      <CheckCircle2 className="w-5 h-5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={handleSignIn}
                  className={`pricing-button ${plan.popular ? "btn-primary" : "btn-secondary"}`}
                >
                  Start Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="cta-background-pattern"></div>
        <div className="container">
          <div className="cta-content">
            <div className="cta-badge">
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              <span>No credit card required</span>
            </div>
            <h2 className="cta-title">
              Ready to Transform Your Testing Process?
            </h2>
            <p className="cta-description">
              Join thousands of teams already using TestNimbus to deliver better
              software faster.
            </p>
            <div className="cta-actions">
              <button
                onClick={handleSignIn}
                className="btn-primary btn-large btn-glow"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
              <p className="cta-note">14-day free trial • Cancel anytime</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3 className="footer-title">TestNimbus</h3>
              <p className="footer-description">
                The modern test management platform for teams that care about
                quality.
              </p>
              <div className="footer-social">
                <button type="button" className="social-link">
                  <Github className="w-5 h-5" />
                </button>
                <button type="button" className="social-link">
                  <Twitter className="w-5 h-5" />
                </button>
                <button type="button" className="social-link">
                  <Linkedin className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="footer-section">
              <h4 className="footer-heading">Product</h4>
              <ul className="footer-links">
                <li>
                  <button type="button" className="text-left">
                    Features
                  </button>
                </li>
                <li>
                  <button type="button" className="text-left">
                    Pricing
                  </button>
                </li>
                <li>
                  <button type="button" className="text-left">
                    Documentation
                  </button>
                </li>
                <li>
                  <button type="button" className="text-left">
                    API
                  </button>
                </li>
              </ul>
            </div>
            <div className="footer-section">
              <h4 className="footer-heading">Company</h4>
              <ul className="footer-links">
                <li>
                  <button type="button" className="text-left">
                    About
                  </button>
                </li>
                <li>
                  <button type="button" className="text-left">
                    Blog
                  </button>
                </li>
                <li>
                  <button type="button" className="text-left">
                    Careers
                  </button>
                </li>
                <li>
                  <button type="button" className="text-left">
                    Contact
                  </button>
                </li>
              </ul>
            </div>
            <div className="footer-section">
              <h4 className="footer-heading">Legal</h4>
              <ul className="footer-links">
                <li>
                  <button type="button" className="text-left">
                    Privacy
                  </button>
                </li>
                <li>
                  <button type="button" className="text-left">
                    Terms
                  </button>
                </li>
                <li>
                  <button type="button" className="text-left">
                    Security
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 TestNimbus. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
